import { useDisplayTimezone } from "@nexus/ui";
import type { ReactNode } from "react";
import {
  ArrowRight,
  Building2,
  LayoutDashboard,
  Pin,
  Plus,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Skeleton, TagList } from "@nexus/ui";
import { dashboardTags, type DashboardRecord } from "@/lib/dashboard/types";
import { cn } from "@/lib/utils";
import { formatRelativeTime, type Perimeter, type RecentDashboard } from "./homeDashboards";

export function dashboardHref(dashboard: DashboardRecord) {
  return `/workspace/dashboards/${encodeURIComponent(dashboard.slug)}`;
}

const FOCUS_RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";

// ─── Balanced grids ───────────────────────────────────────────────────────────

/**
 * Column counts that never leave a lone item on the last row: one item takes the
 * full width, three sit on one row, four split evenly. Spaces are short tiles and
 * fit four across on wide screens; dashboard cards carry more text and stop at three.
 */
const SPACE_GRID: Record<number, string> = {
  1: "grid-cols-1",
  2: "sm:grid-cols-2",
  3: "md:grid-cols-3",
  4: "sm:grid-cols-2 xl:grid-cols-4",
};
const CARD_GRID: Record<number, string> = {
  1: "grid-cols-1",
  2: "sm:grid-cols-2",
  3: "md:grid-cols-3",
  4: "sm:grid-cols-2",
};
/**
 * Compact space links: names never wrap, so beyond four the spaces take two rows
 * (six: three and three) rather than squeezing into one.
 */
const SPACE_LINK_GRID: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
  5: "sm:grid-cols-3",
  6: "sm:grid-cols-3",
};
const GRID_FALLBACK = "sm:grid-cols-2 lg:grid-cols-3";

export function balancedGridClass(count: number, kind: "spaces" | "cards" | "space-links"): string {
  if (kind === "space-links") return SPACE_LINK_GRID[count] ?? "sm:grid-cols-3 lg:grid-cols-4";
  return (kind === "spaces" ? SPACE_GRID : CARD_GRID)[count] ?? GRID_FALLBACK;
}

// ─── Section frame ────────────────────────────────────────────────────────────

export function HomeSection({
  id,
  title,
  badge,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  /** Beside the title: how many there are. */
  badge?: ReactNode;
  /** One quiet line under the title. */
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h2
            id={id}
            className="flex items-center gap-2.5 text-base font-semibold text-brand-navy dark:text-primary"
          >
            {title}
            {badge}
          </h2>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * The way to the full dashboard list: the primary action of Home, styled like the
 * Workspace's "Create dashboard" button.
 */
export function SectionButton({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex density-control-md shrink-0 items-center gap-2 rounded-control bg-action-primary px-4 text-sm font-medium whitespace-nowrap text-action-primary-ink transition-colors hover:bg-action-primary-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )}
    >
      {children}
      <ArrowRight className="size-4" aria-hidden />
    </Link>
  );
}

/** How many there are, beside a section's title ("Dashboards 6"). */
export function CountBadge({ count, noun }: { count: number; noun: string }) {
  return (
    <span className="rounded-full border border-border bg-surface-elevated px-2 py-px text-xs font-semibold text-secondary tabular-nums">
      <span className="sr-only">, </span>
      {count}
      <span className="sr-only"> {noun}</span>
    </span>
  );
}

export function SectionLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex items-center gap-1 rounded-control text-xs font-semibold text-brand-cyan hover:underline",
        FOCUS_RING,
      )}
    >
      {children}
      <ArrowRight className="size-3.5" aria-hidden />
    </Link>
  );
}

// ─── Single space ─────────────────────────────────────────────────────────────

/**
 * The one space of an account that has nothing else to show (a client viewer on an
 * empty system): a full-width entry rather than a grid of one tile, with an
 * optional note under it saying what will appear on Home.
 */
export function SpacePanel({
  to,
  icon: Icon,
  title,
  description,
  note,
}: {
  to: string;
  icon: LucideIcon;
  title: string;
  description: string;
  note?: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-panel border border-border bg-surface">
      <Link
        to={to}
        className={cn(
          "group flex flex-col gap-4 p-5 transition-colors hover:bg-surface-elevated sm:flex-row sm:items-center sm:gap-5 sm:p-6",
          FOCUS_RING,
        )}
      >
        <span className="flex size-11 shrink-0 items-center justify-center rounded-control bg-brand-cyan/10">
          <Icon className="size-5 text-brand-cyan" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-primary">{title}</span>
          <span className="mt-1 block text-sm text-secondary">{description}</span>
        </span>
        <span className="inline-flex items-center gap-1.5 self-start text-sm font-semibold text-brand-cyan group-hover:underline sm:self-center">
          Open {title}
          <ArrowRight className="size-4" aria-hidden />
        </span>
      </Link>
      {note ? (
        <div className="flex items-start gap-3 border-t border-border bg-surface-elevated px-5 py-4 sm:px-6">
          <LayoutDashboard className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
          <div className="text-sm text-secondary">{note}</div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Hero context ─────────────────────────────────────────────────────────────

/** Who the Home is shaped for: the account's profile and whom it covers. */
export function HomeScope({
  profile,
  perimeter,
}: {
  profile: string | undefined;
  perimeter: Perimeter;
}) {
  // A single tenant is named itself (its customer in the tooltip): an account
  // given one tenant, such as a client's war-room account, must see which one.
  const soleTenant =
    !perimeter.allTenants && perimeter.tenants.length === 1 ? perimeter.tenants[0] : undefined;
  const covers = perimeter.allTenants
    ? "All tenants"
    : (soleTenant?.tenant_label ??
      perimeter.organization ??
      `${perimeter.tenants.length} ${perimeter.tenants.length === 1 ? "tenant" : "tenants"}`);
  const detail = perimeter.allTenants
    ? undefined
    : soleTenant
      ? (soleTenant.customer ?? undefined)
      : perimeter.tenants.map((tenant) => tenant.tenant_label).join(", ");
  const showCoverage = perimeter.allTenants || perimeter.tenants.length > 0;
  if (profile !== "nagra-admin" && !showCoverage) return null;
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-secondary">
      {profile === "nagra-admin" ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-2.5 py-1">
          <ShieldCheck className="size-3.5 text-muted" aria-hidden />
          <span className="font-medium">Admin</span>
        </span>
      ) : null}
      {showCoverage ? (
        <span
          title={detail}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-2.5 py-1"
        >
          <Building2 className="size-3.5 text-muted" aria-hidden />
          <span className="font-medium">{covers}</span>
        </span>
      ) : null}
    </div>
  );
}

// ─── Dashboards ───────────────────────────────────────────────────────────────

export function DashboardCard({
  dashboard,
  pinned = false,
}: {
  dashboard: DashboardRecord;
  /** Pinned by the person: marked, as in the dashboard list. */
  pinned?: boolean;
}) {
  const tags = dashboardTags(dashboard);
  return (
    <Link
      to={dashboardHref(dashboard)}
      className={cn(
        "group flex min-w-0 flex-col rounded-panel border border-border bg-surface px-5 py-4 transition-colors hover:border-border-strong",
        FOCUS_RING,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-[0.9375rem] font-semibold text-primary group-hover:underline">
          {dashboard.name}
        </h3>
        <span className="mt-1 flex shrink-0 items-center gap-2">
          {pinned ? (
            <Pin
              className="size-4 fill-interaction-text text-interaction-text"
              role="img"
              aria-label="Pinned"
            />
          ) : null}
          <ArrowRight
            className="size-4 text-muted transition-colors group-hover:text-brand-cyan"
            aria-hidden
          />
        </span>
      </div>
      {tags.length > 0 ? <TagList tags={tags} className="mt-3" /> : null}
    </Link>
  );
}

/**
 * The last place of the dashboard grid when not every dashboard fits: how many
 * more there are and the way to all of them, where the eye already is.
 */
export function ViewAllTile({ to, total, more }: { to: string; total: number; more: number }) {
  return (
    <Link
      to={to}
      className={cn(
        "group flex flex-col justify-center gap-1 rounded-panel border border-dashed border-border-strong px-5 py-4 transition-colors hover:border-interaction-text hover:bg-surface",
        FOCUS_RING,
      )}
    >
      <span className="inline-flex items-center gap-1.5 text-base font-semibold text-interaction-text group-hover:underline">
        View all {total} dashboards
        <ArrowRight className="size-4" aria-hidden />
      </span>
      <span className="text-xs text-muted">
        {more} more · search, tags and owners in the full list
      </span>
    </Link>
  );
}

/**
 * The spaces as one compact row under the dashboards (accounts with several):
 * still one click away, without taking the page from the dashboards.
 */
export function SpaceLinks({
  spaces,
}: {
  spaces: readonly {
    id: string;
    href: string;
    icon: LucideIcon;
    label: string;
    description: string;
  }[];
}) {
  return (
    <nav
      aria-label="Your spaces"
      className={cn("grid grid-cols-1 gap-2.5", balancedGridClass(spaces.length, "space-links"))}
    >
      {spaces.map(({ id, href, icon: Icon, label, description }) => (
        <Link
          key={id}
          to={href}
          title={description}
          className={cn(
            "group flex min-h-12 items-center gap-2.5 rounded-control border border-border bg-surface px-3.5 py-2.5 text-sm font-medium text-primary transition-colors hover:border-brand-cyan/50 hover:bg-surface-elevated",
            FOCUS_RING,
          )}
        >
          <Icon className="size-4 shrink-0 text-brand-cyan" aria-hidden />
          <span className="min-w-0 truncate whitespace-nowrap">{label}</span>
        </Link>
      ))}
    </nav>
  );
}

/** Dashboards opened lately, as one quiet line of links. */
export function RecentLinks({ recent }: { recent: readonly RecentDashboard[] }) {
  const timezone = useDisplayTimezone();
  // One line, always: long names shorten rather than open a second row.
  return (
    <ul className="flex min-w-0 gap-2 overflow-hidden">
      {recent.map(({ dashboard, visitedAt }) => (
        <li key={dashboard.id} className="flex min-w-0 shrink">
          <Link
            to={dashboardHref(dashboard)}
            title={`${dashboard.name} · opened ${formatRelativeTime(visitedAt, Date.now(), timezone)}`}
            className={cn(
              "block max-w-xs min-w-0 truncate rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-secondary transition-colors hover:border-border-strong hover:text-primary",
              FOCUS_RING,
            )}
          >
            {dashboard.name}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function CreateDashboardLink() {
  return (
    <Link
      to="/workspace/dashboards?create=1"
      className={cn(
        "inline-flex items-center gap-1 rounded-control text-xs font-semibold text-brand-cyan hover:underline",
        FOCUS_RING,
      )}
    >
      <Plus className="size-3.5" aria-hidden />
      New dashboard
    </Link>
  );
}

// ─── Loading ──────────────────────────────────────────────────────────────────

/** Placeholder in the shape of what loads: the dashboards' title and a row of cards. */
export function HomeDashboardsSkeleton() {
  return (
    <div role="status" aria-busy="true" aria-label="Loading dashboards">
      <Skeleton className="mb-3 h-5 w-28" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className="flex min-h-20 flex-col gap-3 rounded-panel border border-border bg-surface px-5 py-4"
          >
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="mt-auto h-3 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}
